import { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import SortIcon from "@mui/icons-material/Sort";
import CheckIcon from "@mui/icons-material/Check";
import type { UnassignedSortOrder } from "./unassignedSort.types";

type UnassignedSortDropdownProps = {
  value: UnassignedSortOrder;
  onChange: (order: UnassignedSortOrder) => void;
  zoomLevel: number;
};

const SORT_OPTIONS: { value: UnassignedSortOrder; label: string }[] = [
  { value: "position", label: "Default (drag order)" },
  { value: "dateAsc", label: "Oldest first" },
  { value: "dateDesc", label: "Newest first" },
];

export function UnassignedSortDropdown({
  value,
  onChange,
  zoomLevel,
}: UnassignedSortDropdownProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (order: UnassignedSortOrder) => {
    onChange(order);
    handleClose();
  };

  return (
    <>
      <Tooltip title="Sort unassigned work orders">
        <IconButton
          onClick={handleOpen}
          aria-label="Sort unassigned work orders"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            marginLeft: `${4 * zoomLevel}px`,
            padding: `${2 * zoomLevel}px`,
            width: `${28 * zoomLevel}px`,
            height: `${28 * zoomLevel}px`,
            color: value === "position" ? "#2371E7" : theme.palette.primary.main,
            bgcolor:
              theme.palette.mode === "light"
                ? "#ffffff"
                : "rgba(255, 255, 255, 0.08)",
            borderRadius: `${3 * zoomLevel}px`,
            "&:hover": {
              bgcolor:
                theme.palette.mode === "light"
                  ? "rgba(0, 0, 0, 0.04)"
                  : "rgba(255, 255, 255, 0.12)",
            },
          }}
          size="small"
        >
          <SortIcon
            sx={{
              width: `${16 * zoomLevel}px`,
              height: `${16 * zoomLevel}px`,
              fontSize: `${15 * zoomLevel}px`,
            }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { minWidth: 200, mt: 0.5 },
        }}
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={value === option.value}
            onClick={() => handleSelect(option.value)}
          >
            <ListItemText primary={option.label} />
            {value === option.value && (
              <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
